import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [text, setText] = useState<string>('');
  const [voice, setVoice] = useState<string>('nova');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [errorDialog, setErrorDialog] = useState<{ open: boolean; message: string; statusCode: number | null }>({
    open: false,
    message: '',
    statusCode: null
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && user.name) {
      setUserName(user.name);
    }
  }, []);

  const handleGenerateAudio = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('idToken');
      if (!token) {
        throw new Error('Usuário não autenticado.');
      }

      const response = await fetch('https://ouvi-api.cftechlab.com/openai/text-to-audio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, voice }),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        setErrorDialog({
          open: true,
          message: errorMessage || 'Erro ao gerar áudio.',
          statusCode: response.status
        });
        return;
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      setAudioUrl(audioUrl);
    } catch (error: any) {
      setErrorDialog({
        open: true,
        message: error.message || 'Erro desconhecido.',
        statusCode: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setErrorDialog({ open: false, message: '', statusCode: null });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    if (inputText.length <= 2000) {
      setText(inputText);
    } else {
      setErrorDialog({
        open: true,
        message: 'O texto não pode exceder 2000 caracteres. Essa é uma limitação da versão beta.',
        statusCode: null
      });
    }
  };

  return (
    <div className="dashboard-container">
      <Container maxWidth="md">
        <Box className="dashboard-box">
          <header className="dashboard-header">
            <Typography variant="h4" className="welcome-title">
              Bem-vindo, {userName}!
            </Typography>
            <Typography variant="body1" className="welcome-subtitle">
              Transforme seus textos em áudio natural com nossa tecnologia avançada.
            </Typography>
          </header>

          <main className="dashboard-main">
            <Box className="input-section">
              <label htmlFor="text" className="input-label">
                Seu texto (máximo de 2000 caracteres)
              </label>
              <TextField
                id="text"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={text}
                onChange={handleTextChange}
                className="input-field"
              />
            </Box>

            <Box className="input-section">
              <FormControl fullWidth>
                <InputLabel id="voice-select-label">Selecione a voz</InputLabel>
                <Select
                  labelId="voice-select-label"
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  className="input-field"
                >
                  {/* <MenuItem value="alloy">Alloy</MenuItem>
                  <MenuItem value="ash">Ash</MenuItem>
                  <MenuItem value="coral">Coral</MenuItem>
                  <MenuItem value="echo">Echo</MenuItem>
                  <MenuItem value="fable">Fable</MenuItem> */}
                  <MenuItem value="onyx">Onyx</MenuItem>
                  <MenuItem value="nova">Nova</MenuItem>
                  {/* <MenuItem value="sage">Sage</MenuItem>
                  <MenuItem value="shimmer">Shimmer</MenuItem> */}
                </Select>
              </FormControl>
            </Box>

            <Box className="button-section">
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateAudio}
                disabled={loading || !text}
                className="generate-button"
              >
                {loading ? 'Gerando áudio...' : 'Gerar Áudio'}
              </Button>
            </Box>

            {audioUrl && (
              <Box className="audio-section">
                <Typography variant="body1">Áudio gerado com sucesso!</Typography>
                <audio controls src={audioUrl} className="audio-player" />
                <Button
                  variant="outlined"
                  color="secondary"
                  href={audioUrl}
                  download="audio.mp3"
                  className="download-button"
                >
                  Baixar Áudio
                </Button>
              </Box>
            )}
          </main>
        </Box>

        <Dialog
          open={errorDialog.open}
          onClose={handleCloseDialog}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Erro</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {errorDialog.statusCode && <strong>Código do erro: {errorDialog.statusCode}</strong>}
            </DialogContentText>
            <DialogContentText>{errorDialog.message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary" autoFocus>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
};

export default Dashboard;
